import RichText from "@@/components/RichText";
import Box from "@@/elements/Box";
import { ChevronUp } from "@@/elements/Icons/icons";
import Typography from "@@/elements/Typography";
import OptimizelyBlock, {
  OptimizelyBlockProps,
} from "@@/integrations/optimizly/Block";
import EpiProperty from "@@/integrations/optimizly/Property";
import cn from "classnames";
import { useEffect, useRef, useState } from "react";
import styles from "./faqitem.module.scss";

let faqIdCounter = 0;

export type FaqItemProperties = {
  header: string;
  body: string;
};

export type FaqOwnProps = {
  active?: boolean;
  className?: string;
  background: "grey" | "white" | "blue";
  anchorId?: string;
};

export type FaqProps = FaqOwnProps & FaqItemProperties;

const FaqItem = ({
  header, body, active = false, className, background, anchorId,
}: FaqProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [uniqueId] = useState(() => anchorId || `faq-item-${++faqIdCounter}`);
  const triggerId = `${uniqueId}-trigger`;
  const contentId = `${uniqueId}-content`;
  let contentHeight: number = 0;

  const close = () => {
    setIsOpen(false);
  };

  const open = () => {
    setIsOpen(true);
  };

  useEffect(() => {
    if (active && active === true) {
      open();
    } else {
      close();
    }
  }, []);

  useEffect(() => {
    contentHeight = contentRef.current.scrollHeight;
    if (isOpen === false) {
      contentRef.current.style.maxHeight = `${contentHeight}px`;
      contentRef.current.style.maxHeight = "0px";
    } else {
      contentRef.current.style.maxHeight = `${0}px`;
      contentRef.current.style.maxHeight = `${contentHeight}px`;
    }
  }, [isOpen]);

  const handleClick = () => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  };

  return (
    <Box
      id={anchorId}
      className={cn(className, styles.faqItem, {
        [styles["is-active"]]: isOpen,
        [styles[`${background}`]]: true,
      })}
      component="section"
      container
      flexDirection="column"
      py={6}
      mb={4}
    >
      <Box
        px={6}>
        <EpiProperty<FaqItemProperties> name="header">
          <button
            id={triggerId}
            type="button"
            className={styles.faqItem__header}
            aria-expanded={isOpen}
            aria-controls={contentId}
            onClick={handleClick}
          >
            <Typography
              variant="heading4"
              asElement="span">
              {header}
            </Typography>
            <span
              className={styles.faqItem_actionbutton}
              aria-hidden="true"
            >
              <ChevronUp />
            </span>
          </button>
        </EpiProperty>
        <div
          id={contentId}
          role="region"
          aria-labelledby={triggerId}
          className={styles.faqItem__content}
          ref={contentRef}
          inert={isOpen ? undefined : ""}>
          <EpiProperty<FaqItemProperties> name="body">
            <Box pt={4}>
              <RichText body={body} />
            </Box>
          </EpiProperty>
        </div>
      </Box>
    </Box>
  );
};

export type ProvidedFaqProps = OptimizelyBlockProps<FaqItemProperties> &
FaqProps;

const ProvidedFaq = ({
  blockId,
  inEditMode,
  properties,
  anchorId,
  ...rest
}: ProvidedFaqProps) => (
  <OptimizelyBlock
    blockId={blockId}
    inEditMode={inEditMode}
    properties={properties}
    anchorId={anchorId}
  >
    {(optimizlyProperties) => (
      <FaqItem
        anchorId={anchorId}
        {...rest}
        {...optimizlyProperties}
        {...(inEditMode
          ? {
            active: true,
          }
          : {})}
      />
    )}
  </OptimizelyBlock>
);

export default ProvidedFaq;
